import { Injectable } from '@angular/core';
import {BehaviorSubject, Observable} from "rxjs";

@Injectable({
  providedIn: 'root'
})
export class AccessibilityService {
  static readonly defaultScaleValue = 1;
  static readonly defaultTextScaleValue = 1;
  static readonly maxTextScaleValue = 4;
  static readonly minTextScaleValue = 1;
  static readonly textScaleValueStep = 0.25;
  static readonly minScaleValue = 0.5;
  static readonly maxScaleValue = 2;
  static readonly scaleValueStep = 0.25;

  private _scaleValue = new BehaviorSubject(AccessibilityService.defaultScaleValue);
  private _textScaleValue = new BehaviorSubject(AccessibilityService.defaultTextScaleValue);
  private _grayscale = new BehaviorSubject(false);
  private _highContrast = new BehaviorSubject(false);
  private _stopMovingContent = new BehaviorSubject(false);
  private _textSpacing = new BehaviorSubject(false);

  private readonly LOCAL_STORAGE_KEYS = {
    fontScale: "A11Y_FONT_SCALE",
    textFontScale: "A11Y_TEXT_FONT_SCALE",
    grayscale: "A11Y_GRAYSCALE",
    highContrast: "A11Y_HIGH_CONTRAST",
    movingContent: "A11Y_MOVING_CONTENT",
    textSpacing: "A11Y_TEXT_SPACING",
  }

  constructor() {
    this.loadLocalStorageValues();
  }

  updateScaleValue(value: number): number {
    value = this.validateScaleValue(value);
    this._scaleValue.next(value);
    this.setLocalStorageValue(this.LOCAL_STORAGE_KEYS.fontScale, value.toString());
    return value;
  }

  validateScaleValue(value: number) {
    if(value > AccessibilityService.maxScaleValue) value = AccessibilityService.maxScaleValue;
    else if(value < AccessibilityService.minScaleValue) value = AccessibilityService.minScaleValue;

    return value;
  }

  getNextStepScaleValue(): number {
    let nextVal = this._scaleValue.value + AccessibilityService.scaleValueStep;
    nextVal = this.validateScaleValue(nextVal);
    return nextVal;
  }

  getPrevStepScaleValue(): number {
    let nextVal = this._scaleValue.value -AccessibilityService.scaleValueStep;
    nextVal = this.validateScaleValue(nextVal);
    return nextVal;
  }

  resetScaleValue(): number {
    return this.updateScaleValue(AccessibilityService.defaultScaleValue);
  }

  getScaleValue(): Observable<number> {
    return this._scaleValue;
  }

  loadLocalStorageValues() {
    const scaleValueStr = window.localStorage.getItem(this.LOCAL_STORAGE_KEYS.fontScale);
    if(scaleValueStr && scaleValueStr.length > 0) {
      let scaleValue = parseFloat(scaleValueStr);
      if(!isNaN(scaleValue)) {
        scaleValue = this.validateScaleValue(scaleValue);
        this.updateScaleValue(scaleValue);
      }
    }

    const textScaleValueStr = window.localStorage.getItem(this.LOCAL_STORAGE_KEYS.textFontScale);
    if(textScaleValueStr && textScaleValueStr.length > 0) {
      let textScaleValue = parseFloat(textScaleValueStr);
      if(!isNaN(textScaleValue)) {
        textScaleValue = this.validateTextScaleValue(textScaleValue);
        this.updateTextScaleValue(textScaleValue);
      }
    }

    const grayscaleStr = window.localStorage.getItem(this.LOCAL_STORAGE_KEYS.grayscale);
    if(grayscaleStr && grayscaleStr.length > 0) {
      this._grayscale.next(true);
    }

    const highContrastStr = window.localStorage.getItem(this.LOCAL_STORAGE_KEYS.highContrast);
    if(highContrastStr && highContrastStr.length > 0) {
      this._highContrast.next(true);
    }

    const stopMovingContentStr = window.localStorage.getItem(this.LOCAL_STORAGE_KEYS.movingContent);
    if(stopMovingContentStr && stopMovingContentStr.length > 0) {
      this._stopMovingContent.next(true);

    }

    const textSpacingStr = window.localStorage.getItem(this.LOCAL_STORAGE_KEYS.textSpacing);
    if(textSpacingStr && textSpacingStr.length > 0) {
      this._textSpacing.next(true);
    }
  }

  private setLocalStorageValue(key: string, value: string) {
    window.localStorage.setItem(key, value);
  }

  getGrayscaleValue(): Observable<boolean> {
    return this._grayscale;
  }

  updateGrayscaleValue(value: boolean) {
    this.setLocalStorageValue(this.LOCAL_STORAGE_KEYS.grayscale, value ? '1' : '');
    this._grayscale.next(value);
  }

  getHighContrastValue(): Observable<boolean> {
    return this._highContrast;
  }

  updateHighContrastValue(value: boolean) {
    this.setLocalStorageValue(this.LOCAL_STORAGE_KEYS.highContrast, value ? '1' : '');
    this._highContrast.next(value);
  }

  getStopMovingContent(): Observable<boolean> {
    return this._stopMovingContent;
  }

  updateStopMovingContent(value: boolean) {
    this.setLocalStorageValue(this.LOCAL_STORAGE_KEYS.movingContent, value ? '1' : '');
    this._stopMovingContent.next(value);
  }


  getTextSpacingValue(): Observable<boolean> {
    return this._textSpacing;
  }

  updateTextSpacingValue(value: boolean) {
    this.setLocalStorageValue(this.LOCAL_STORAGE_KEYS.textSpacing, value ? '1' : '');
    this._textSpacing.next(value);
  }












  updateTextScaleValue(value: number): number {
    value = this.validateTextScaleValue(value);
    this._textScaleValue.next(value);
    this.setLocalStorageValue(this.LOCAL_STORAGE_KEYS.textFontScale, value.toString());
    return value;
  }

  validateTextScaleValue(value: number) {
    if(value > AccessibilityService.maxTextScaleValue) value = AccessibilityService.maxTextScaleValue;
    else if(value < AccessibilityService.minTextScaleValue) value = AccessibilityService.minTextScaleValue;

    return value;
  }

  getNextStepTextScaleValue(): number {
    let nextVal = this._textScaleValue.value + AccessibilityService.textScaleValueStep;
    nextVal = this.validateTextScaleValue(nextVal);
    return nextVal;
  }

  getPrevStepTextScaleValue(): number {
    let nextVal = this._textScaleValue.value -AccessibilityService.textScaleValueStep;
    nextVal = this.validateTextScaleValue(nextVal);
    return nextVal;
  }

  resetTextScaleValue(): number {
    return this.updateTextScaleValue(AccessibilityService.defaultTextScaleValue);
  }

  getTextScaleValue(): Observable<number> {
    return this._textScaleValue;
  }
}
